import React, { FC } from 'react'
import classNames from 'classnames'
import { ImageCardProps } from './types'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import VideoAnimation from '@toasttab/shared/src/components/atoms/VideoAnimation/VideoAnimation'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import { LottieProps } from '@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer.d'
const LottiePlayer: FC<LottieProps> =
  typeof window !== 'undefined' || process.env.NODE_ENV === 'test'
    ? require('@toasttab/shared/src/components/atoms/LottiePlayer/LottiePlayer')
        .default
    : () => null

const SearchCard = ({
  badge,
  bgColor,
  cardBorderColor,
  ctaLabel,
  ctaLink,
  dataTestId = 'SearchCard-1',
  elevation,
  featuredImage,
  headingBadge,
  image,
  imageRound,
  linkIcons,
  loopAnimation,
  padding,
  sectionDescription,
  sectionHeading,
  slideIsActive,
  textAlignment = 'left'
}: ImageCardProps) => {
  const renderContentImage = () => {
    let imageRoundClass = imageRound
      ? 'rounded-lg lg:rounded-md'
      : 'rounded-t-lg lg:rounded-t-md'
    const imgClasses = classNames(
      image[0].classes,
      imageRoundClass,
      'h-[160px] sm:h[180px] w-auto mx-auto object-contain'
    )
    switch (image[0].extension) {
      case 'json':
        return (
          <LottiePlayer
            classes={imageRoundClass}
            corners={imageRound}
            direction={1}
            hover
            lottieFile={image[0].url}
            previewImage={featuredImage}
          />
        )
      case 'mp4':
      case 'webm':
        return (
          <div className={`${imageRoundClass} w-full relative overflow-hidden`}>
            <VideoAnimation
              animation={image[0]}
              autoPlay
              classes={`${imageRoundClass} w-full h-full object-cover`}
              corners
              loop={loopAnimation}
              previewImage={featuredImage}
            />
          </div>
        )
      default: {
        if (image[0].optimizedImages) {
          return (
            <img
              alt=''
              className={imgClasses}
              src={`${image[0].optimizedImages.srcWebp}`}
            />
          )
        }

        if (image[0].url) {
          return <img alt='' className={imgClasses} src={`${image[0].url}`} />
        }
        return null
      }
    }
  }

  const wrapperClasses = classNames(
    'flex flex-col justify-between p-6 ',
    Card.backgroundColor(bgColor),
    Card.borderColor(cardBorderColor),
    Card.elevation(elevation),
    {
      border: Card.borderColor(cardBorderColor) !== '',
      'hover:shadow-3xl':
        ctaLink && ctaLabel && Card.borderColor(cardBorderColor) !== '',
      'rounded-md': imageRound,
      'rounded-t-md': !imageRound
    }
  )
  let tabIndex = 0
  if (slideIsActive) {
    tabIndex = slideIsActive ? 0 : -1
  }

  return (
    <div
      className={wrapperClasses}
      data-testid={dataTestId}
      tabIndex={tabIndex}
    >
      <div>
        <div className='grid items-center mb-6 h-[160px] sm:h[180px]'>
          {image?.length > 0 && renderContentImage()}
        </div>
        <div
          className={`${Text.alignment(textAlignment)} ${Card.cardPadding(
            padding
          )}`}
        >
          <div>
            {badge && headingBadge && (
              <Badge
                text={badge}
                badgeColor='bg-sky-0'
                classes='mb-4'
                uppercase={true}
              />
            )}
            <Typography
              as='h4'
              variation='h4'
              classes='text-default mb-2 sm:mb-4 !font-normal'
            >
              {sectionHeading}
            </Typography>
            <Typography
              as='p'
              variation='default'
              classes='text-secondary mb-6'
            >
              {sectionDescription}
            </Typography>
            {badge && !headingBadge && (
              <div className='mb-3'>
                <Badge text={badge} badgeColor='bg-sky-0' uppercase={true} />
              </div>
            )}
          </div>
        </div>
      </div>
      {ctaLink && ctaLabel && (
        <div>
          <TextLink
            href={ctaLink}
            icon={linkIcons}
            text={ctaLabel}
            tabIndex={tabIndex}
          />
        </div>
      )}
    </div>
  )
}

export default SearchCard
